import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function RoomsManagerDialog({ open, onOpenChange, rooms, onAddRoom, onRenameRoom, onDeleteRoom }) {
  const [newRoom, setNewRoom] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const add = async () => {
    const name = newRoom.trim();
    if (!name) return;
    await onAddRoom(name);
    setNewRoom("");
  };

  const doRename = async () => {
    const name = renameValue.trim();
    if (!renameTarget || !name) return;
    if (name !== renameTarget.name) await onRenameRoom(renameTarget, name);
    setRenameTarget(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Manage rooms</DialogTitle>
            <DialogDescription className="sr-only">Add, rename or remove cleaning rooms</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {rooms.map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2" data-testid={`room-row-${r.id}`}>
                <span className="flex-1 font-semibold text-ink">{r.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => {
                    setRenameTarget(r);
                    setRenameValue(r.name);
                  }}
                  data-testid={`room-rename-${r.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-destructive"
                  onClick={() => onDeleteRoom(r)}
                  data-testid={`room-delete-${r.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Input
              value={newRoom}
              onChange={(e) => setNewRoom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="New room name"
              className="rounded-xl"
              data-testid="new-room-input"
            />
            <Button className="rounded-xl" onClick={add} data-testid="add-room-btn">
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Rename room</DialogTitle>
            <DialogDescription className="sr-only">Enter a new name for this room</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doRename()}
            className="rounded-xl"
            data-testid="rename-room-input"
          />
          <DialogFooter>
            <Button className="rounded-xl" onClick={doRename} data-testid="rename-room-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
